import {
    IManufacturerResponse,
    IOutputSensorDataConfigSdResponse,
    TransducerSensitivityInputOutput,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import { RegisterOptions } from 'react-hook-form';
import { AutoCompleteValue } from 'shared/autocomplete/AutoComplete';
import { createNotesRules } from 'utils/form';
import { Filter } from 'utils/query_v3';
import { insertSpacesOnCaseChange } from 'utils/string';

export const NotesMaxLength = 500;

export const notesRules = createNotesRules(NotesMaxLength);

export const DisplayNameMaxLength = 64;

export const displayNameRules: RegisterOptions = {
    required: 'Display name is required.',
    maxLength: {
        value: DisplayNameMaxLength,
        message: `Display name cannot exceed ${DisplayNameMaxLength} characters.`,
    },
};

export const ManufacturerMaxLength = 128;
export const SerialNumberMaxLength = 128;
export const ModelNumberMaxLength = 64;

export const validateManufacturer = (value: AutoCompleteValue<IManufacturerResponse>) => {
    if (typeof value === 'string' && value.length > ManufacturerMaxLength) {
        return `Manufacturer cannot exceed ${ManufacturerMaxLength} characters.`;
    }

    return undefined;
};

const validateManufacturerNotDuplicate = async (
    value: AutoCompleteValue<IManufacturerResponse>,
) => {
    const lengthValidation = validateManufacturer(value);
    if (lengthValidation) {
        return lengthValidation;
    }

    if (typeof value === 'object') {
        return undefined;
    }

    const existing = await sdClient.getManufacturers({
        filter: Filter.caseInsensitiveEq('Name', value),
    });

    if (existing.Total > 0) {
        return `Manufacturer with name "${value}" already exists. Please enter a unique name.`;
    }
};

export const manufacturerRules: RegisterOptions = {
    required: 'Please select a manufacturer.',
    validate: validateManufacturerNotDuplicate,
};

export const modelRules: RegisterOptions = {
    required: 'Model Number is required.',
    maxLength: {
        value: ModelNumberMaxLength,
        message: `Model Number cannot exceed ${ModelNumberMaxLength} characters.`,
    },
};

export function formatSensorRole(sensorRole: string): string {
    if (sensorRole.startsWith('AtoDChannel')) {
        // forgive me for this hackery
        // TODO @jp: export this from SDK or make a route, dear god.
        return `A to D Channel ${sensorRole[sensorRole.length - 1]}`;
    }
    return insertSpacesOnCaseChange(sensorRole);
}

export const getPhysicalMeasurementRangeDisplay = (
    config: IOutputSensorDataConfigSdResponse,
) => {
    const sensitivity = config.TransducerSensitivity;
    if (!(sensitivity instanceof TransducerSensitivityInputOutput)) {
        throw new Error(
            'analog sensor did not have input-output sensitivity configuration',
        );
    }
    return `${sensitivity.MeasurementLow} - ${sensitivity.MeasurementHigh} ${config.Unit.Abbreviation}`;
};

export const getTransducerOutputRangeDisplay = (
    config: IOutputSensorDataConfigSdResponse,
) => {
    const sensitivity = config.TransducerSensitivity;
    if (!(sensitivity instanceof TransducerSensitivityInputOutput)) {
        throw new Error(
            'analog sensor did not have input-output sensitivity configuration',
        );
    }
    return `${sensitivity.OutputLow} - ${sensitivity.OutputHigh} ${config.TransducerOutputUnit.Abbreviation}`;
};
